import { useLocale } from '@mirai/locale';
import { Notification, Progress, styles, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { SCRIPT_ID } from './FormCybersource.constants';
import { L10N } from './FormCybersource.l10n';
import * as style from './FormCybersource.module.css';
import { ERROR } from '../../../../../Core.constants';
import { loadScript } from '../../../../../helpers/loadScript';

export const FormCybersource = ({ response, ...others }) => {
  const { translate } = useLocale();

  const [error, setError] = useState();
  const [paymentReady, setPaymentReady] = useState(false);
  const [ready, setReady] = useState(false);

  const clientRef = useRef();
  const checkoutRef = useRef();

  useEffect(() => {
    if (!response) return;

    let mounted = true;
    let cleanup;

    (async () => {
      try {
        const { configuration: { clientLibrary, clientLibraryIntegrity } = {} } = response;
        cleanup = await loadScript({
          async: false,
          crossOrigin: 'anonymous',
          dataset: { cybersourceSdk: 'true' },
          domain: 'payments',
          id: SCRIPT_ID,
          integrity: clientLibraryIntegrity,
          src: clientLibrary,
          onLoad: () => setReady(true),
        });
        if (mounted && window.VAS?.UnifiedCheckout) setReady(true);
      } catch (nextError) {
        setError(nextError);
      }
    })();

    return () => {
      mounted = false;
      cleanup && cleanup();
      checkoutRef.current?.destroy();
      clientRef.current?.destroy();
      checkoutRef.current = null;
      clientRef.current = null;
    };
  }, [response]);

  useEffect(() => {
    if (!ready || !response) return;

    const { configuration: { sessionJwt } = {} } = response;

    (async () => {
      try {
        setError();
        setPaymentReady(false);
        const client = await window.VAS.UnifiedCheckout(sessionJwt);
        const checkout = await client.createCheckout({ autoProcessing: true });
        clientRef.current = client;
        checkoutRef.current = checkout;
        setPaymentReady(true);
        const token = await checkout.mount({
          paymentSelection: '#payment-methods',
          paymentScreen: '#payment-form',
        });
        if (token) {
          const { configuration: { paymentTransactionId, returnUrl } = {} } = response;
          const url = new URL(returnUrl);
          paymentTransactionId && url.searchParams.append('paymentTransactionId', paymentTransactionId);
          url.searchParams.append('token', token);

          window.location.href = url.toString();
        } else {
          setError(ERROR.UNKNOWN);
        }
      } catch (nextError) {
        setError(nextError);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  return (
    <View {...others} className={styles(others.className, style.container)}>
      <Progress indeterminate visible={!paymentReady && !error} />
      <View id="payment-methods" />
      <View id="payment-form" />
      {error && (
        <Notification error small wide>
          {translate(L10N.NOTIFICATION_ERROR)}
        </Notification>
      )}
    </View>
  );
};

FormCybersource.displayName = 'Mirai:Payments:ButtonPayment:FormCybersource';

FormCybersource.propTypes = {
  response: PropTypes.shape(),
};
